import { computed, onMounted, onUnmounted, type Ref, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { getWindow } from '@/helpers/globals'
import { useSegment } from '@/third-party/segment.ts'

// ATTENTION!
// This logic is complicated due to the fact tha PLP pages somehow mutate
// the data object when navigating from PLP to PDP.
// It is necessary to fire page events once after the data is fetched and
// the correct page title is computed.

export function useSegmentPage(
    title: Ref<string>,
    data: Ref<unknown>,
    fetching: Ref<boolean>,
    stale: Ref<boolean>
): void {
    const { page } = useSegment()
    const fetchingOrStale = computed(() => fetching.value || stale.value)
    const isSet = ref(false)

    const isActive = ref(true)

    const hasNavigatedAway = ref(false)

    const router = useRouter()

    router.beforeEach((to, from) => {
        if (to.meta.name && from.meta.name && to.meta.name === from.meta.name) {
            // We have WR / ER that have two routes on the same component
            // The route names are different, however, for the sake of
            // Suspense working correctly, we set route.meta.name
            // If it's the same, the user has not navigated away
            return
        }

        if (to.name !== from.name) {
            hasNavigatedAway.value = true
        }
    })

    onMounted(() => {
        isActive.value = true
        hasNavigatedAway.value = false

        if (fetchingOrStale.value) {
            return
        }

        if (isSet.value) {
            isSet.value = false
            return
        }

        const window = getWindow()

        page({
            url: window?.location.href,
            path: window?.location.pathname,
            title: title.value,
        })
    })

    onUnmounted(() => {
        isActive.value = false
        isSet.value = false
    })

    watch(
        data,
        (newData) => {
            if (fetchingOrStale.value) {
                return
            }

            if (!newData) {
                return
            }

            if (!isActive.value) {
                return
            }

            isSet.value = true

            if (hasNavigatedAway.value) {
                return
            }

            const window = getWindow()

            page({
                url: window?.location.href,
                path: window?.location.pathname,
                title: title.value,
            })
        },
        { immediate: true }
    )
}
